import { withNamespaces } from "react-i18next";
import { useState } from "react";
import {
    Badge,
    Button,
    Col,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    UncontrolledTooltip
} from "reactstrap";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import {
    API_BASE_URL,
    API_URL_RISK_AI_GET_ADDITIONAL_DETAILS,
    CHAT_BOT_DOMAIN,
    FRIA_STATUS,
    PROVIDER_CONFORMITY_STATUS
} from "src/modules/data-protection/constants";
import RiskAssessmentQuestionnaire from "./components/RiskAssessmentQuestionnaire";
import FriaQuestionnaire from "./components/FriaQuestionnaire";
import ConformityAssessmentQuestionnaire from "./components/ConformityAssessmentQuestionnaire";
import GetHelpFromAIIcon from "src/modules/data-protection/assets/images/microchip/black.png";
import AIRiskAssessmentModal from "src/modules/data-protection/components/RisksAndTasks/risks/ai/AssessmentModal";
import { RiRobotLine } from "react-icons/ri";
import AssessmentSection from "../components/AssessmentSection";
import { getFriaSectionState } from "./helpers/getFriaSectionState";
import { fetchQuestionnaireQuestions } from "./helpers/fetchQuestionnaireQuestions";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const CompleteWithAIConfirmationModal = ({
    t,
    isOpen,
    toggle,
    onConfirm,
    isLoading = false,
    title,
    description,
    helperText
}) => {
    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="md"
        >
            <ModalHeader toggle={toggle}>
                {title}
            </ModalHeader>

            <ModalBody>
                <div className="text-center">
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: 72,
                            height: 72,
                            background: "rgba(13, 110, 253, 0.1)"
                        }}
                    >
                        <RiRobotLine
                            size={36}
                            className="text-primary"
                        />
                    </div>

                    <h5 className="mb-3">
                        {t("Generate AI Suggestions")}
                    </h5>

                    <p className="text-muted mb-0">
                        {description}
                    </p>

                    {helperText && (
                        <small className="text-muted d-block mt-3">
                            {helperText}
                        </small>
                    )}
                </div>
            </ModalBody>

            <ModalFooter>
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={isLoading}
                >
                    {t("Cancel")}
                </Button>

                <Button
                    color="primary"
                    onClick={onConfirm}
                    disabled={isLoading}
                >
                    {isLoading
                        ? t("Generating...")
                        : t("Generate with AI")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const Questionnaires = ({
    t,
    token,
    onValidSubmit,
    aiRiskAssessmentId,
    enableQuestionnaire,
    Organization,
    filledWithAIBefore,
    isQuestionnaireCompleted,
    friaOptions,
    conformityOptions,
    providerConformityRequired,
    complianceObligations = []
}) => {
    const { softwareId } = useParams();
    const history = useHistory();

    const [isCreateAIOpen, setIsCreateAIOpen] = useState(false);
    const [isFriaCreateAIOpen, setIsFriaCreateAIOpen] = useState(false);
    const [aiBasedRiskAssessmentModalStatus, setAiBasedRiskAssessmentModalStatus] = useState(false);

    const {
        friaRequired,
        hasFriaAssessment,
        friaStatus,
        friaReportCaseId
    } = friaOptions;

    const {
        providerConformityStatus,
        conformityReportCaseId
    } = conformityOptions;

    const isCeConformityEnabled = isFeatureEnabledForActiveModule("ce-conformity");
    const showCeConformityAssessment = isCeConformityEnabled && providerConformityRequired;

    const friaSectionState = getFriaSectionState({
        friaRequired,
        hasFriaAssessment,
        friaStatus
    });

    const friaStatusBadge = FRIA_STATUS[friaStatus] || {
        color: "secondary",
        label: "Unknown"
    };

    const conformityStatusBadge = PROVIDER_CONFORMITY_STATUS[providerConformityStatus] || {
        color: "secondary",
        label: providerConformityStatus || "Unknown"
    };

    const sectionsIds = [
        "ai_risk_assessment"
    ];

    const questionsQueries = useQueries({
        queries: sectionsIds.map((sectionId) => {
            return {
                queryKey: ["data-protection-report-ai-assessment-questions-list", sectionId, softwareId],
                queryFn: () => fetchQuestionnaireQuestions(sectionId, softwareId),
                enabled: true,
                refetchOnWindowFocus: false
            };
        })
    });

    const risksAssessmentQuery = questionsQueries[0];

    const friaQuestionsQuery = useQuery({
        queryKey: ["data-protection-report-ai-assessment-questions-list", "fria_assessment", softwareId],
        queryFn: () => fetchQuestionnaireQuestions("fria_assessment", softwareId),
        enabled: friaSectionState.active,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching questions."), {
                type: "error"
            });
        }
    });

    const conformityQuestionsQuery = useQuery({
        queryKey: ["data-protection-conformity-assessment-questions-list", softwareId],
        queryFn: () => fetchQuestionnaireQuestions("conformity_assessment", softwareId),
        enabled: showCeConformityAssessment,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching questions."), {
                type: "error"
            });
        }
    });

    const completeWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/report_case/fill_ai_act`,
                {
                    report_case_id: aiRiskAssessmentId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess: () => {
            risksAssessmentQuery.refetch();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsCreateAIOpen(false);
        }
    });

    const completeFriaWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/report_case/fill_fria`,
                {
                    report_case_id: friaReportCaseId
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data;
        },
        onSuccess: () => {
            friaQuestionsQuery.refetch();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsFriaCreateAIOpen(false);
        }
    });

    return (
        <div className="pt-2 mt-3">
            <CompleteWithAIConfirmationModal
                isOpen={isCreateAIOpen}
                toggle={() => setIsCreateAIOpen(false)}
                onConfirm={completeWithAIMutation.mutate}
                isLoading={completeWithAIMutation.isLoading}
                t={t}
                title={t("Complete Risk Assessment with AI")}
                description={t("Would you like to complete the risk assessment using AI suggestions?")}
                helperText={t("AI will analyze the available information and generate responses for the risk assessment questionnaire. The generated responses will be applied automatically and can be reviewed or updated later.")}
            />

            <CompleteWithAIConfirmationModal
                isOpen={isFriaCreateAIOpen}
                toggle={() => setIsFriaCreateAIOpen(false)}
                onConfirm={completeFriaWithAIMutation.mutate}
                isLoading={completeFriaWithAIMutation.isLoading}
                t={t}
                title={t("Complete FRIA with AI")}
                description={t("Would you like to complete the FRIA assessment using AI suggestions?")}
                helperText={t("AI will analyze the available information and generate responses for the FRIA questionnaire. The generated responses will be saved automatically and can be reviewed and updated later.")}
            />

            {aiBasedRiskAssessmentModalStatus && (
                <AIRiskAssessmentModal
                    closeModal={() => setAiBasedRiskAssessmentModalStatus(false)}
                    onRisksCreated={() => {
                        history.push(`/admin/data-protection/softwares/${softwareId}?tab=risk-assessment`);
                    }}
                    isOpen={aiBasedRiskAssessmentModalStatus}
                    translateResult={true}
                    aiRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/gdpr/ai-risk-assessment/risks-associated-questioner.ai`,
                        payload: {
                            asset: softwareId,
                            organizationId: Organization.id,
                            questionnaireSlug: "ai_risk_assessment",
                            regenerate_risk_ai: undefined
                        }
                    }}
                    aiTranlateRequestOptions={{
                        requestUrl: `${CHAT_BOT_DOMAIN}api/gdpr/risks-associated-questioner-translate.ai`,
                        payload: {
                            assetId: softwareId,
                            module: "GDPR_AI_RISK_ASSESSMENT",
                            organizationId: Organization.id
                        }
                    }}
                    additionnalDetailsRequestOptions={{
                        requestUrl: API_URL_RISK_AI_GET_ADDITIONAL_DETAILS,
                        payload: {}
                    }}
                    createRisksRequestOptions={{
                        requestUrl: `${API_BASE_URL}/risk_rate/create_ai`,
                        payload: {
                            software: softwareId,
                            reportCase: aiRiskAssessmentId
                        }
                    }}
                >
                    {(result) => {
                        return (
                            <Modal
                                className="ai-risk-result-modal mw-100"
                                style={{ width: "90%" }}
                                isOpen={aiBasedRiskAssessmentModalStatus}
                                size="xl"
                            >
                                <ModalHeader toggle={() => setAiBasedRiskAssessmentModalStatus(!aiBasedRiskAssessmentModalStatus)}>
                                    {t("Risk Assesment Support by AI")}
                                </ModalHeader>

                                <ModalBody>
                                    <result.type {...result.props} />
                                </ModalBody>
                            </Modal>
                        );
                    }}
                </AIRiskAssessmentModal>
            )}

            <AssessmentSection
                id="eu-ai-act-risk-assessment"
                title={t("EU AI Act Risk Assessment")}
                subtitle={t("Determine whether this AI system falls under the EU AI Act and classify its risk level.")}
            >
                <Row className="ai-act-questionnaire-panel__toolbar">
                    <Col
                        sm="12"
                        className="d-flex align-items-center justify-content-end flex-wrap gap-2"
                    >
                        <Button
                            color="primary"
                            className="ai-base-risk-assessment-button"
                            onClick={() => setIsCreateAIOpen(true)}
                            disabled={risksAssessmentQuery.isFetching || filledWithAIBefore}
                        >
                            {t("Complete Risk Assessment with AI")}
                        </Button>

                        <div id="get-help-from-ai-button">
                            <Button
                                color="light"
                                className="ai-base-risk-assessment-button"
                                onClick={() => {
                                    setAiBasedRiskAssessmentModalStatus(true);
                                }}
                                outline
                                disabled={risksAssessmentQuery.isFetching || !isQuestionnaireCompleted}
                            >
                                <img alt="ai icon" src={GetHelpFromAIIcon} />
                                {t("Get help from AI")}
                            </Button>

                            {!isQuestionnaireCompleted && (
                                <UncontrolledTooltip
                                    target="get-help-from-ai-button"
                                >
                                    {t("Complete the questionnaire to generate AI-powered risks, recommendations, and tasks.")}
                                </UncontrolledTooltip>
                            )}
                        </div>
                    </Col>
                </Row>

                <div className="ai-act-questionnaire-panel__questionnaire">
                    <RiskAssessmentQuestionnaire
                        questionsQuery={risksAssessmentQuery}
                        softwareId={softwareId}
                        aiRiskAssessmentId={aiRiskAssessmentId}
                        enableQuestionnaire={enableQuestionnaire}
                        onValidSubmit={onValidSubmit}
                        setIsCreateAIOpen={setIsCreateAIOpen}
                        complianceObligations={complianceObligations}
                    />
                </div>
            </AssessmentSection>

            <AssessmentSection
                id="fria-assessment"
                title={t("FRIA")}
                subtitle={t("Assess the fundamental rights impact of this high-risk AI system.")}
            >
                {!friaSectionState.active ? (
                    <div className="ai-act-questionnaire-panel__disabled-notice">
                        <div className="d-flex align-items-start gap-2 text-muted">
                            <i
                                className="ri-information-line fs-5 flex-shrink-0"
                                id="fria-section-disabled-info"
                            />

                            <span>{t(friaSectionState.messageKey)}</span>

                            <UncontrolledTooltip
                                target="fria-section-disabled-info"
                            >
                                {t(friaSectionState.tooltipKey)}
                            </UncontrolledTooltip>
                        </div>
                    </div>
                ) : (
                    <>
                        <Row className="ai-act-questionnaire-panel__toolbar align-items-center g-2">
                            <Col xs="12" md="6">
                                <Badge
                                    color={friaStatusBadge.color}
                                    className={`bg-${friaStatusBadge.color}`}
                                    pill
                                >
                                    {t(friaStatusBadge.label)}
                                </Badge>
                            </Col>

                            <Col
                                xs="12"
                                md="6"
                                className="d-flex justify-content-md-end"
                            >
                                <Button
                                    color="primary"
                                    className="ai-base-risk-assessment-button"
                                    outline
                                    disabled={friaQuestionsQuery.isFetching}
                                    onClick={() => setIsFriaCreateAIOpen(true)}
                                >
                                    <img alt="ai icon" src={GetHelpFromAIIcon} />
                                    {t("Complete FRIA with AI")}
                                </Button>
                            </Col>
                        </Row>

                        <div className="ai-act-questionnaire-panel__questionnaire">
                            <FriaQuestionnaire
                                questionsQuery={friaQuestionsQuery}
                                softwareId={softwareId}
                                friaReportCaseId={friaReportCaseId}
                                onValidSubmit={onValidSubmit}
                            />
                        </div>
                    </>
                )}
            </AssessmentSection>

            {showCeConformityAssessment && (
                <AssessmentSection
                    id="ce-conformity-assessment"
                    title={t("CE Conformity Assessment")}
                    subtitle={t("Document provider obligations required for CE marking and EU market placement.")}
                >
                    <Row className="ai-act-questionnaire-panel__toolbar">
                        <Col xs="12">
                            <Badge
                                color={conformityStatusBadge.color}
                                className={`bg-${conformityStatusBadge.color}`}
                                pill
                            >
                                {t(conformityStatusBadge.label)}
                            </Badge>
                        </Col>
                    </Row>

                    <div className="ai-act-questionnaire-panel__questionnaire">
                        <ConformityAssessmentQuestionnaire
                            questionsQuery={conformityQuestionsQuery}
                            softwareId={softwareId}
                            conformityReportCaseId={conformityReportCaseId}
                            onValidSubmit={onValidSubmit}
                            complianceObligations={complianceObligations}
                        />
                    </div>
                </AssessmentSection>
            )}
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization } = state;

    return {
        token,
        user,
        Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        Questionnaires
    )
);
